import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';

const APP_STORE_BADGE_URL =
  'https://lirp.cdn-website.com/ceae0154/dms3rep/multi/opt/Apple+App+Store+download+button-1920w.png';
const GOOGLE_PLAY_BADGE_URL =
  'https://lirp.cdn-website.com/ceae0154/dms3rep/multi/opt/Google+Store+download+button-1920w.png';
const BADGE_SIZES = '180px';

// ============================================================================
// Content schema (self-contained literal — statically extractable at preview
// time). The slot group definitions used by the hooks are derived from it
// below so there is a single source of truth.
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentAccess',
  displayName: 'Resident Access',
  tokens: {
    'residentaccess.app_heading': {
      label: 'Mobile App Heading',
      type: 'text',
      defaultValue: 'Download the Resident Mobile App',
    },
  },
  slotGroups: {
    'residentaccess.login_link': {
      label: 'Login Button',
      fields: {
        label: { label: 'Label', type: 'text', defaultValue: 'Login Now' },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url', 'property_online_leasing_url'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'residentaccess.app_store_link': {
      label: 'App Store Link',
      fields: {
        url: {
          label: 'App Store URL',
          type: 'url',
          defaultValue: 'https://apps.apple.com/us/app/loft-living/id333244247',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'residentaccess.play_store_link': {
      label: 'Google Play Link',
      fields: {
        url: {
          label: 'Google Play URL',
          type: 'url',
          defaultValue: 'https://play.google.com/store/apps/details?id=com.activebuilding.residents',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
};

const groups = contentSchema.slotGroups as Record<string, SlotGroupDefinition>;
const loginLinkDef = groups['residentaccess.login_link'];
const appStoreLinkDef = groups['residentaccess.app_store_link'];
const playStoreLinkDef = groups['residentaccess.play_store_link'];

// ============================================================================
// Component
// ============================================================================

export function ResidentAccess() {
  const loginLink = useSlotGroup('residentaccess.login_link', loginLinkDef);
  const appStoreLink = useSlotGroup('residentaccess.app_store_link', appStoreLinkDef);
  const playStoreLink = useSlotGroup('residentaccess.play_store_link', playStoreLinkDef);

  useRevealStyles();
  useComfyCtaStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  const loginLabel = loginLink.label || 'Login Now';
  const appStoreHref = appStoreLink.url || '';
  const playStoreHref = playStoreLink.url || '';

  return (
    <section className="ra-section">
      <div ref={sectionRef} className="reveal-fade-up ra-container">
        <div className="ra-actions">
          {loginLink.url ? (
            <EditableSlotGroup groupPath="residentaccess.login_link" definition={loginLinkDef}>
              <a
                href={loginLink.url}
                target={loginLink._target || '_blank'}
                rel={loginLink._target === '_self' ? undefined : 'noopener noreferrer'}
                className="comfy-cta ra-login-cta"
              >
                {loginLabel}
              </a>
            </EditableSlotGroup>
          ) : (
            <EditableSlotGroup groupPath="residentaccess.login_link" definition={loginLinkDef}>
              <span
                className="comfy-cta ra-login-cta ra-login-cta--disabled"
                aria-disabled="true"
              >
                {loginLabel}
              </span>
            </EditableSlotGroup>
          )}
        </div>

        {/* Mobile app row */}
        <div className="ra-app-row">
          <EditableToken path="residentaccess.app_heading" as="h3" className="ra-app-heading">
            Download the Resident Mobile App
          </EditableToken>
          <div className="ra-app-badges">
            <EditableSlotGroup
              groupPath="residentaccess.app_store_link"
              definition={appStoreLinkDef}
            >
              {appStoreHref ? (
                <a
                  href={appStoreHref}
                  target={appStoreLink._target || '_blank'}
                  rel={appStoreLink._target === '_self' ? undefined : 'noopener noreferrer'}
                  className="ra-badge-link"
                >
                  <VibesitesImage
                    url={APP_STORE_BADGE_URL}
                    alt="Download on the App Store"
                    sizes={BADGE_SIZES}
                    className="ra-badge-img"
                  />
                </a>
              ) : (
                <span className="ra-badge-link ra-badge-link--muted">
                  <VibesitesImage
                    url={APP_STORE_BADGE_URL}
                    alt="Download on the App Store"
                    sizes={BADGE_SIZES}
                    className="ra-badge-img"
                  />
                </span>
              )}
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="residentaccess.play_store_link"
              definition={playStoreLinkDef}
            >
              {playStoreHref ? (
                <a
                  href={playStoreHref}
                  target={playStoreLink._target || '_blank'}
                  rel={playStoreLink._target === '_self' ? undefined : 'noopener noreferrer'}
                  className="ra-badge-link"
                >
                  <VibesitesImage
                    url={GOOGLE_PLAY_BADGE_URL}
                    alt="Get it on Google Play"
                    sizes={BADGE_SIZES}
                    className="ra-badge-img"
                  />
                </a>
              ) : (
                <span className="ra-badge-link ra-badge-link--muted">
                  <VibesitesImage
                    url={GOOGLE_PLAY_BADGE_URL}
                    alt="Get it on Google Play"
                    sizes={BADGE_SIZES}
                    className="ra-badge-img"
                  />
                </span>
              )}
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .ra-section {
          padding: 3rem 0 4rem;
          background: white;
        }
        @media (min-width: 768px) {
          .ra-section { padding: 4rem 0 5rem; }
        }
        .ra-container {
          max-width: 78rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .ra-container { padding: 0 1.5rem; } }

        .ra-actions {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .ra-login-cta {
          min-width: 14rem;
        }
        .ra-login-cta--disabled {
          opacity: 0.6;
          cursor: not-allowed;
          pointer-events: none;
        }

        .ra-app-row {
          margin-top: 3.5rem;
          text-align: center;
        }
        @media (min-width: 768px) {
          .ra-app-row { margin-top: 4.5rem; }
        }
        .ra-app-heading {
          margin: 0 0 1.75rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2vw + 1rem, 2.25rem);
          font-weight: 400;
          color: var(--vibe-color-heading, var(--vibe-color-text, #1a2540));
        }

        .ra-app-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 0.85rem 1rem;
          justify-content: center;
          align-items: center;
        }

        .ra-badge-link {
          display: inline-flex;
          line-height: 0;
          text-decoration: none;
          transition: transform 0.25s ease, opacity 0.25s ease;
        }
        .ra-badge-link:hover {
          transform: translateY(-2px);
          opacity: 0.92;
        }
        .ra-badge-link--muted {
          cursor: default;
        }

        .ra-badge-img {
          height: 2.75rem;
          width: auto;
          display: block;
          object-fit: contain;
        }
        @media (min-width: 768px) {
          .ra-badge-img { height: 3.25rem; }
        }
      `}</style>
    </section>
  );
}

(ResidentAccess as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ResidentAccess;
